// "Ready to deploy": what each release channel of a project's map would
// ship, from its issues' `awaits` (Poulpe-556). Only the map's channels
// count, as today's Awaiting filter has it; a Done or archived issue
// awaits nothing.
import type { Issue, ProjectColumn, ReleaseMap } from '../api/types'
import { issueChannels } from '../lib/releases'
import { CLOSED_STATES } from '../vocab'
import { deriveColumns, type ColumnData, type FilterContext } from './filters'
import type { BoardView } from './view'

/** One channel of the map, by its name, and the issues it would ship. */
export interface ChannelShipment {
  channel: string
  issues: Issue[]
}

function awaiting(issue: Issue, project: string | null): boolean {
  return issue.project === project && !issue.archived_at && issue.state !== 'done'
}

/** For each channel of the map, in the map's order, the given issues of the project that await it, in the order given. */
export function shipments(project: string | null, issues: Iterable<Issue>, map: ReleaseMap | null | undefined): ChannelShipment[] {
  const channels = map?.channels.map((c) => c.name) ?? []
  const found = new Map(channels.map((channel) => [channel, [] as Issue[]]))
  for (const issue of issues) {
    if (!awaiting(issue, project)) continue
    for (const channel of issueChannels(issue).awaits) found.get(channel)?.push(issue)
  }
  return channels.map((channel) => ({ channel, issues: found.get(channel) ?? [] }))
}

/** The ids of the project's issues that await a channel of its map. */
export function readyToDeploy(project: string | null, issues: Iterable<Issue>, map: ReleaseMap | null | undefined): Set<string> {
  return new Set(shipments(project, issues, map).flatMap((s) => s.issues.map((i) => i.id)))
}

/**
 * The board's columns under "Ready to deploy": every awaiting issue shows,
 * however long ago it was closed, so that the board holds what the chip
 * counts. No terminal column keeps only its recent issues, and a Cancelled or
 * Duplicate column, or a column of those types, shows when it holds one.
 */
export function deployColumns(issues: Iterable<Issue>, view: BoardView, ctx: FilterContext, now: number, own: readonly ProjectColumn[] = []): ColumnData[] {
  const whole: BoardView = { ...view, closed: true, showAll: ['done', ...CLOSED_STATES, ...own.map((c) => c.id)] }
  return deriveColumns(issues, whole, ctx, now, own).filter((c) => view.closed || !CLOSED_STATES.includes(c.state) || c.items.length > 0)
}
